import { Button, Col, Dialog, Icon, Input, Spinner, Stack, Tappable, Text, useTheme } from "@rally/ui-kit";
import { useCallback, useEffect, useState } from "react";
import { failureSaid } from "../../localization/failure.ts";
import { say } from "../../localization/say.ts";
import { calledBy } from "../../me/called.ts";
import { act, read } from "../../net/rally.ts";
import { goTo } from "../../router.ts";
import { type Addable, chosenOf, offered, toggled } from "./adding.ts";

type Agent = { account: string; handle: string; model: string; owner: string };

type Found = {
  people: Array<{ account: string; handle: string; displayName: string | null; avatar: string | null }>;
};

export function AddSomebody({
  conversation,
  visible,
  here,
  me,
  mayAdd,
  onDismiss,
  onAdded,
}: {
  conversation: string;
  visible: boolean;

  here: readonly string[];

  me: string;

  mayAdd: boolean;
  onDismiss: () => void;

  onAdded: (many: number) => void;
}) {
  const [agents, setAgents] = useState<Agent[] | null>(null);
  const [people, setPeople] = useState<Addable[]>([]);
  const [fragment, setFragment] = useState("");
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set());
  const [wrong, setWrong] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const readAgents = useCallback(() => {
    read<Agent[]>("my.agents")
      .then(setAgents)
      .catch(() => setAgents([]));
  }, []);

  useEffect(() => {
    if (!visible) return;
    setFragment("");
    setChosen(new Set());
    setWrong(null);
    readAgents();
  }, [visible, readAgents]);

  useEffect(() => {
    if (!visible) return;
    const want = fragment.trim();
    if (want === "") {
      setPeople([]);
      return;
    }
    let dropped = false;
    void read<Found>("search", { q: want })
      .then((said) => {
        if (dropped) return;
        setPeople(
          said.people.map((one) => ({
            account: one.account,
            handle: one.handle,
            displayName: one.displayName,
            avatar: one.avatar,
            kind: "person" as const,
            model: null,
          })),
        );
      })
      .catch(() => {
        if (!dropped) setPeople([]);
      });
    return () => {
      dropped = true;
    };
  }, [visible, fragment]);

  const asAddable: Addable[] = (agents ?? []).map((one) => ({
    account: one.account,
    handle: one.handle,
    displayName: null,
    avatar: null,
    kind: "agent" as const,
    model: one.model,
  }));

  const who = { people, agents: asAddable, here, me, mayAdd };
  const sections = offered(who, fragment);
  const picked = chosen.size;

  async function addThem(): Promise<void> {
    const going = chosenOf(who, chosen);
    if (going.length === 0) return;
    setBusy(true);
    setWrong(null);
    let added = 0;
    try {
      for (const one of going) {
        await act("conversation.addParticipant", { conversation, account: one.account });
        added += 1;
        if (one.kind === "agent") {
          await act("agent.share", {
            account: one.account,
            scope: "conversation",
            id: conversation,
          }).catch(() => undefined);
        }
      }
      onAdded(added);
      onDismiss();
    } catch (cause) {
      if (added > 0) onAdded(added);
      setWrong(failureSaid(cause, say("add.broke")));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      visible={visible}
      onDismiss={onDismiss}
      title={say("add.title")}
      testID="add-an-agent"
      footer={
        <Button
          onPress={() => void addThem()}
          loading={busy}
          disabled={picked === 0}
          testID="add-somebody-add"
        >
          {picked === 0 ? say("add.pressNone") : say("add.press")(picked)}
        </Button>
      }
    >
      <Stack gap={3}>
        <Text typeName="caption" color="text.secondary">
          {say("add.what")}
        </Text>
        {mayAdd ? null : (
          <Text typeName="caption" color="text.tertiary" testID="add-somebody-refused">
            {say("add.notYours")}
          </Text>
        )}
        {wrong ? (
          <Text typeName="caption" color="danger">
            {wrong}
          </Text>
        ) : null}
        <Input
          value={fragment}
          onChangeText={setFragment}
          placeholder={say("add.filter")}
          autoFocus
          testID="add-somebody-filter"
        />
        {agents === null ? <Spinner label={say("agents.loading")} /> : null}

        <Section
          what={say("add.people")}
          rows={sections.people}
          chosen={chosen}
          onToggle={(account) => setChosen(toggled(chosen, account))}
          empty={fragment.trim() === "" ? say("add.findPeople") : say("add.nobody")}
        />
        <Section
          what={say("add.agents")}
          rows={sections.agents}
          chosen={chosen}
          onToggle={(account) => setChosen(toggled(chosen, account))}
          empty={(agents ?? []).length === 0 ? say("add.noAgents") : say("add.allHere")}
        />
        {(agents ?? []).length === 0 && agents !== null ? (
          <Button
            variant="soft"
            icon="bot"
            testID="add-agent-make-one"
            onPress={() => {
              onDismiss();
              goTo("/me/agents");
            }}
          >
            {say("add.makeOne")}
          </Button>
        ) : null}
      </Stack>
    </Dialog>
  );
}

function Section({
  what,
  rows,
  chosen,
  onToggle,
  empty,
}: {
  what: string;
  rows: readonly Addable[];
  chosen: ReadonlySet<string>;
  onToggle: (account: string) => void;
  empty: string;
}) {
  const theme = useTheme();
  return (
    <Col style={{ gap: theme.space[1] }}>
      <Text typeName="label" color="text.tertiary">
        {what}
      </Text>
      {rows.length === 0 ? (
        <Text typeName="caption" color="text.tertiary">
          {empty}
        </Text>
      ) : (
        rows.map((one) => (
          <PersonChoice key={one.account} one={one} chosen={chosen.has(one.account)} onToggle={onToggle} />
        ))
      )}
    </Col>
  );
}

function PersonChoice({
  one,
  chosen,
  onToggle,
}: {
  one: Addable;
  chosen: boolean;
  onToggle: (account: string) => void;
}) {
  const theme = useTheme();
  const name = calledBy({ handle: one.handle, displayName: one.displayName }) ?? one.handle;
  return (
    <Tappable
      accessibilityRole="checkbox"
      accessibilityState={{ checked: chosen }}
      accessibilityLabel={chosen ? say("add.chosen")(name) : say("add.notChosen")(name)}
      testID={`add-somebody-${one.handle}`}
      onPress={() => onToggle(one.account)}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: theme.space[2],
        minHeight: 44,
        paddingHorizontal: theme.space[2],
        borderRadius: theme.radius.sm,
      }}
    >
      <Col
        style={{
          width: 20,
          height: 20,
          borderRadius: theme.radius.xs,
          borderWidth: 2,
          borderColor: chosen ? theme.color.accent : theme.color["border.strong"],
          backgroundColor: chosen ? theme.color.accent : "transparent",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {chosen ? <Icon name="check" size={14} color="bg.app" /> : null}
      </Col>
      <Icon name={one.kind === "agent" ? "bot" : "user"} size={16} color="text.tertiary" />
      <Col style={{ flex: 1, minWidth: 0 }}>
        <Text typeName="body" color="text.primary" numberOfLines={1}>
          {name}
        </Text>
        {one.model ? (
          <Text typeName="caption" color="text.tertiary" numberOfLines={1}>
            {one.model}
          </Text>
        ) : null}
      </Col>
    </Tappable>
  );
}
